import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CentimetersToInchesConverter = () => {
  const [inputData, setInputData] = useState({
    centimeters: '',
    inches: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const convertToInches = () => {
    const { centimeters } = inputData;

    // Validate input
    if (!centimeters) {
      setError('Please enter a value in centimeters');
      return;
    }

    const cleanCentimeters = centimeters.replace(/\s/g, '');
    if (isNaN(cleanCentimeters) || cleanCentimeters <= 0) {
      setError('Please enter a valid positive number');
      return;
    }

    // Convert to inches
    const inches = parseFloat(cleanCentimeters) / 2.54;
    const result = inches.toFixed(2);

    // Create visualization data
    const values = [parseFloat(cleanCentimeters), parseFloat(result)];
    const labels = ['Centimeters', 'Inches'];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Value',
        data: values,
        backgroundColor: ['rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)'],
        borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Enter the value in centimeters',
      `Centimeters: ${cleanCentimeters}`,
      '\nStep 2: Convert to inches using the formula',
      'Inches = Centimeters / 2.54',
      `\nStep 3: Calculate the result`,
      `Inches: ${result}`
    ];

    setResults({
      originalCentimeters: cleanCentimeters,
      inches: result,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    centimeters: '10',
    inches: '3.94'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Centimeters to Inches Converter | Unit Conversion Tool</title>
          <meta name="description" content="Convert centimeters to inches easily with this tool. This converter helps in quick unit conversion for length measurements." />
          <meta name="keywords" content="centimeters to inches, unit conversion, length converter, measurement tool" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">📐</div>
            <h1 className="text-3xl font-bold text-gray-800">Centimeters to Inches Converter</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Convert centimeters to inches easily. This tool helps in quick unit conversion for length measurements.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Centimeters:</label>
              <input
                type="text"
                name="centimeters"
                value={inputData.centimeters}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter value in centimeters (e.g., 10)"
              />
            </div>
          </div>

          <button
            onClick={convertToInches}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Convert
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Result</h3>
                  <p className="text-lg">Original Centimeters: {results.originalCentimeters}</p>
                  <p className="text-lg">Inches: {results.inches}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Value Comparison</h3>
                  <p className="text-lg">Centimeters: {results.originalCentimeters}</p>
                  <p className="text-lg">Inches: {results.inches}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Unit'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Centimeters vs Inches'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for converting centimeters to inches:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Input Centimeters: {exampleData.centimeters}</li>
                <li>Converted Inches: {exampleData.inches}</li>
              </ul>

              <h4 className="mb-4">Conversion Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the conversion formula: Inches = Centimeters / 2.54</li>
                <li>Example: {exampleData.centimeters} cm = {exampleData.inches} inches</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Centimeters to Inches Converter:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Centimeters:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your value in centimeters</li>
                  <li>Spaces are automatically removed</li>
                </ul>
              </li>
              <li><strong>Click Convert:</strong> The converter will:
                <ul className="list-disc pl-6 mt-2">
                  <li>Convert the value to inches</li>
                  <li>Display the result</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the converted value in inches</li>
                  <li>View the visual comparison of values</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure accurate input for precise results</li>
                <li>Use the tool for quick conversions</li>
                <li>Record your conversions for future reference</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Unit Conversion <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Unit conversion is essential in various fields, including engineering, science, and everyday life. It helps in:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Standardization:</strong> Ensuring consistent measurements</li>
              <li><strong>Interoperability:</strong> Facilitating communication across different systems</li>
              <li><strong>Accuracy:</strong> Providing precise measurements</li>
              <li><strong>Practicality:</strong> Simplifying calculations and comparisons</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your input values</li>
              <li><strong>Documentation:</strong> Record your conversions</li>
              <li><strong>Validation:</strong> Verify results when critical</li>
              <li><strong>Implementation:</strong> Follow system requirements</li>
            </ul>
            <p className="italic mt-4">
              Note: Always verify your conversions, especially when using them in critical applications.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CentimetersToInchesConverter;